import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Heart, ShieldCheck, Award, Truck, Sparkles, ChevronRight } from "lucide-react";

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const { addToCart, toggleWishlist, wishlist } = useStore();

  useEffect(() => {
    setImg(0);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (p === false) return <div className="max-w-4xl mx-auto py-32 text-center text-[#C2B29F]">Product not found.</div>;
  if (!p) return <div className="max-w-4xl mx-auto py-32 text-center text-[#C2B29F]">Loading…</div>;
  const wished = wishlist.includes(p.id);
  const discount = p.original_price ? Math.round((1 - p.price / p.original_price) * 100) : 0;

  return (
    <div data-testid="product-detail" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
      <div className="text-xs tracking-[0.14em] uppercase text-[#C2B29F] mb-6 flex items-center gap-2">
        <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
        <ChevronRight size={12}/>
        <Link to="/shop" className="hover:text-[#D4AF37]">Shop</Link>
        <ChevronRight size={12}/>
        <Link to={`/shop/${p.category}`} className="hover:text-[#D4AF37] capitalize">{p.category}</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="aspect-square bg-[#221712] overflow-hidden gold-glow">
            <img src={p.images?.[img]} alt={p.name} className="w-full h-full object-cover"/>
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {p.images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setImg(i)}
                  className={`aspect-square bg-[#221712] overflow-hidden border ${img === i ? "border-[#D4AF37]" : "border-transparent"} hover:border-[#D4AF37]/60 transition`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <div className="eyebrow">{p.metal}{p.gemstone ? ` · ${p.gemstone}` : ""}</div>
          <h1 className="font-serif-luxe text-3xl sm:text-4xl text-[#FAF8F5] mt-3 leading-tight">{p.name}</h1>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-serif-luxe text-3xl text-[#D4AF37]">{formatINR(p.price)}</span>
            {p.original_price && <>
              <span className="text-[#C2B29F] line-through text-lg">{formatINR(p.original_price)}</span>
              <span className="text-xs tracking-[0.14em] uppercase text-[#D4AF37]">Save {discount}%</span>
            </>}
          </div>
          <div className="text-xs text-[#C2B29F] mt-1">Inclusive of all taxes · Making charges included</div>

          <p className="mt-6 text-sm text-[#C2B29F] leading-relaxed">{p.description}</p>

          <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
            {p.weight_grams && <div><div className="eyebrow">Gross Weight</div><div className="text-[#FAF8F5] mt-1">{p.weight_grams} g</div></div>}
            {p.diamond_carat && <div><div className="eyebrow">Diamond Carat</div><div className="text-[#FAF8F5] mt-1">{p.diamond_carat} ct</div></div>}
            {p.purity && <div><div className="eyebrow">Purity</div><div className="text-[#FAF8F5] mt-1">{p.purity}</div></div>}
            {p.certification && <div><div className="eyebrow">Certification</div><div className="text-[#FAF8F5] mt-1">{p.certification}</div></div>}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button data-testid="add-to-cart" onClick={() => addToCart(p)} className="btn-gold flex-1">Add to Bag</button>
            <button data-testid="wishlist-toggle" onClick={() => toggleWishlist(p.id)} className="btn-outline-gold sm:w-14 !px-4">
              <Heart size={16} className={wished ? "fill-[#D4AF37] text-[#D4AF37]" : ""}/>
            </button>
          </div>

          <div className="mt-8 border-t border-[#D4AF37]/15 pt-6 grid grid-cols-1 gap-3 text-xs">
            <div className="flex items-center gap-3 text-[#FAF8F5]"><ShieldCheck size={16} className="text-[#D4AF37]"/> BIS Hallmarked · Lifetime Authenticity Guarantee</div>
            <div className="flex items-center gap-3 text-[#FAF8F5]"><Award size={16} className="text-[#D4AF37]"/> Certified diamonds (IGI / GIA)</div>
            <div className="flex items-center gap-3 text-[#FAF8F5]"><Truck size={16} className="text-[#D4AF37]"/> Complimentary insured shipping in 3–7 days</div>
            <div className="flex items-center gap-3 text-[#FAF8F5]"><Sparkles size={16} className="text-[#D4AF37]"/> Free polishing & lifetime care</div>
          </div>

          <div className="mt-8 text-[10px] tracking-[0.24em] uppercase text-[#C2B29F]">Handcrafted in Jaipur · Designed by Lakshya Soni</div>
        </div>
      </div>
    </div>
  );
}
